"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { getStreamUrl, type NowPlaying } from "@/lib/api";
import { useElapsedSeconds } from "@/lib/use-elapsed-seconds";
import { AlbumArt } from "@/components/dashboard/album-art";

// The Home dashboard's "Now Playing" player — per the approved mockup
// (docs/design/station-manager-mockup.html), this card gets real
// playback controls, not just a text readout. What "playback" means for
// a live broadcast is narrower than an on-demand player, though: there's
// nothing to skip/seek/rewind — it's one continuous Icecast stream — so
// this is Play/Pause (start or stop listening in this browser tab,
// independent of whether the station itself is on air) plus volume,
// centered in the card, not the mockup's prev/rewind/fast-forward/next
// transport row.
//
// The elapsed-time progress (both the numbers and the waveform bars'
// played/unplayed split) IS real, not simulated: GET
// /playout/now-playing returns started_at (stamped server-side the
// moment radio.liq reported this track change) and duration_seconds
// (from the matched track's own file); elapsed = now - started_at,
// ticked client-side every second. When there's no duration (no track
// match, e.g. the emergency fallback), the whole progress section is
// hidden rather than faked.
//
// The waveform's bar HEIGHTS, unlike the played/unplayed split, are
// cosmetic — this isn't real audio amplitude data, just like the
// approved mockup's own waveform (its own code comment says "Generate a
// static waveform," random heights). Seeded off the track id so a given
// track always draws the same shape instead of reshuffling on every
// poll, but it's texture, not a measurement — not seekable, since you
// can't seek a live broadcast.

// Each bar is flex-1 (stretches to fill the row — see the waveform JSX
// below), so its actual pixel width is just card-width / BAR_COUNT. A
// higher count makes each bar proportionally finer without touching
// anything else.
// Thin bars: 2px wide with a 1px gap, so the count follows the available width
// (measured below) instead of being fixed.
const BAR_PITCH = 3;
const DEFAULT_BAR_COUNT = 90;

// Waveform gradient: teal on the left blending into blue on the right.
const WAVE_FROM = [20, 184, 166]; // #14b8a6
const WAVE_TO = [37, 99, 235]; // #2563eb

function waveColor(t: number): string {
  const c = WAVE_FROM.map((from, i) => Math.round(from + (WAVE_TO[i] - from) * t));
  return `rgb(${c[0]} ${c[1]} ${c[2]})`;
}

function formatTime(seconds: number): string {
  const clamped = Math.max(0, Math.floor(seconds));
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function seededBarHeights(seed: string, count: number): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const heights: number[] = [];
  for (let i = 0; i < count; i++) {
    h = (h * 1664525 + 1013904223) >>> 0; // LCG step
    const frac = (h >>> 8) / 0xffffff;
    heights.push(10 + Math.round(frac * 46)); // 10-56px in the 60px row
  }
  return heights;
}

export function RadioPlayer({ nowPlaying }: { nowPlaying: NowPlaying | null }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const lastVolume = useRef(0.8);
  const [playError, setPlayError] = useState<string | null>(null);

  const elapsed = useElapsedSeconds(nowPlaying?.started_at);

  const waveRef = useRef<HTMLDivElement | null>(null);
  const [barCount, setBarCount] = useState(DEFAULT_BAR_COUNT);
  const hasWave = !!nowPlaying?.title && !!nowPlaying?.duration_seconds;
  useEffect(() => {
    const el = waveRef.current;
    if (!el) return;
    const update = () =>
      setBarCount(Math.max(20, Math.floor((el.clientWidth + 1) / BAR_PITCH)));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasWave]);

  const barHeights = useMemo(
    () => seededBarHeights(nowPlaying?.track_id ?? "none", barCount),
    [nowPlaying?.track_id, barCount]
  );

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  async function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    setPlayError(null);
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    try {
      // Setting src fresh on every play (rather than once up front) joins
      // the stream live, same as opening the URL directly — it doesn't
      // replay from wherever a long-paused tab's buffer left off.
      audio.src = getStreamUrl();
      await audio.play();
      setIsPlaying(true);
    } catch {
      setPlayError("Couldn't start playback — check that the stream is reachable.");
    }
  }

  const duration = nowPlaying?.duration_seconds ?? null;
  const hasProgress = duration !== null && duration > 0;
  const playedRatio = hasProgress ? Math.min(1, elapsed / duration) : 0;

  function toggleMute() {
    if (volume > 0) {
      lastVolume.current = volume;
      setVolume(0);
    } else {
      setVolume(lastVolume.current || 0.8);
    }
  }

  return (
    <div className="@container flex flex-col gap-5">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a live radio stream has no captions track */}
      <audio
        ref={audioRef}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
      />

      {nowPlaying?.title ? (
        // Art on the left; title/artist/album at the top of the right-hand
        // column and the waveform pinned to its bottom, so the waveform's
        // baseline lines up with the bottom of the art (items-stretch +
        // justify-between).
        <div className="flex items-stretch gap-4">
          <AlbumArt
            trackId={nowPlaying.track_id}
            hasCoverArt={nowPlaying.has_cover_art}
            size="lg"
            className="h-40 w-40 rounded-none shadow-md @lg:h-44 @lg:w-44"
          />
          <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="truncate text-lg font-bold @lg:text-xl">{nowPlaying.title}</div>
              <div className="truncate text-sm font-medium text-accent-blue">{nowPlaying.artist}</div>
              {nowPlaying.album && (
                <div className="truncate text-xs text-text-tertiary">
                  {nowPlaying.album}
                  {nowPlaying.year ? ` · ${nowPlaying.year}` : ""}
                </div>
              )}
            </div>

            {hasProgress && (
              <div className="flex flex-col gap-1">
                {/* Bars are centred on the midline (items-center) so they
                    extend above and below it like a real waveform; flex-1
                    makes them stretch to the available width. Heights are
                    cosmetic (seeded off the track id), the played/unplayed
                    split is real. Colour runs teal -> blue left to right;
                    unplayed bars are the same colour, faded. */}
                <div ref={waveRef} className="flex h-[60px] w-full items-center justify-between gap-[1px]" aria-hidden="true">
                  {barHeights.map((h, i) => (
                    <span
                      key={i}
                      className="w-[2px] shrink-0 rounded-full"
                      style={{
                        height: `${h}px`,
                        backgroundColor: waveColor(i / Math.max(1, barCount - 1)),
                        opacity: i / barCount < playedRatio ? 1 : 0.22,
                      }}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[11px] text-text-tertiary">
                  <span>{formatTime(elapsed)}</span>
                  <span>{formatTime(duration!)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-center text-sm text-text-tertiary">Nothing reported as playing yet.</p>
      )}

      <div className="flex flex-col items-center gap-2.5">
        <div className="flex items-center gap-5">
          {/* Volume: just the speaker icon until hovered (or focused, for
              keyboard users). The 40px wrapper holds the icon's place in
              the row so nothing else moves; the pill inside is anchored to
              its right edge and widens leftwards on hover, so the icon
              slides left and the slider appears inline to its right. */}
          <div className="group relative h-10 w-10 shrink-0">
            <div className="absolute right-0 top-0 flex h-10 w-10 items-center overflow-hidden rounded-full bg-transparent transition-[width,background-color] duration-200 ease-out group-focus-within:w-[124px] group-focus-within:bg-accent-blue/10 group-hover:w-[124px] group-hover:bg-accent-blue/10">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={volume === 0 ? "Unmute" : "Mute"}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-accent-blue"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                  <path d="M11 5L6 9H2v6h4l5 4V5Z" />
                  {volume === 0 ? (
                    <path d="m16 9 5 6m0-6-5 6" />
                  ) : (
                    <>
                      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                      {volume > 0.5 && <path d="M18.5 5.5a9 9 0 0 1 0 13" />}
                    </>
                  )}
                </svg>
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="mr-3 h-1.5 w-[72px] shrink-0 cursor-pointer accent-accent-blue"
                aria-label="Volume"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={togglePlay}
            disabled={!nowPlaying?.title}
            aria-label={isPlaying ? "Pause" : "Play"}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-blue text-white shadow-md transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" className="h-6 w-6">
                <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
                <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 translate-x-[1px]">
                <path d="M8 5v14l11-7Z" />
              </svg>
            )}
          </button>

          {/* Placeholder for "add to favourites" — deliberately inert for now. */}
          <button
            type="button"
            disabled
            aria-label="Add to favourites (coming soon)"
            title="Favourites — coming soon"
            className="flex h-10 w-10 cursor-not-allowed items-center justify-center rounded-full text-slate-300"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            </svg>
          </button>
        </div>

        {isPlaying && (
          <span className="text-xs font-medium text-text-tertiary">Listening in this tab</span>
        )}
      </div>

      {playError && <p className="text-center text-xs text-accent-red">{playError}</p>}
    </div>
  );
}
